import { isNonAdminSalesRep } from "./isNonAdminSalesRep";

// Pure version of the home-routing decision that used to live inline in
// Routes.js. Extracted so we can unit-test the matrix (including the new
// sales-rep skip on the three peptides branches) without rendering the
// whole Routes tree (which transitively imports `heic-to` and breaks Jest
// — see memory note heic-to-jest-esm-workaround).
//
// Branch parity with Routes.js:165-195:
//   1. Peptides-only org short-circuit
//   2. Encounters → default Home
//   3. Sales rep + ordersSalesDashboard → salesRep
//   4. ordersManagement → manufacturer board
//   5. orders → either provider board OR peptides (peptides && !dme split)
//   6. Final peptides fallback
//
// The three peptides-resolving branches (1, 5b, 6) now skip non-admin
// sales reps to prevent the redirect loop where they'd land on /peptides,
// our route guard would bounce them to /salesDashboard, and (if missing
// the ordersSalesDashboard feature) /salesDashboard would bounce them
// back to /.
export function computeHomeRoute(auth, { hasFeature }) {
  const productLines = auth?.orgInfo?.productLines;
  const blockPeptides = isNonAdminSalesRep(auth);

  // Branch 0: Patients (order-patient plan) have exactly one home — the
  // peptides dashboard. Their per-patient org is NOT provisioned with
  // productLines (that's a clinic concept, set only by
  // Order - Provider/Manufacturer - Signup; Order - Patient - Signup sets
  // none), so every productLines-based branch below would fall through to
  // `null` and Routes.js would leave `home` at its "default" → the EHR Home.
  // Route on the authoritative session plan instead — the same signal
  // usePeptidePricing / useFeatures / withProviderOnlyGuard.js:24 use to
  // identify a patient. Checked first because a patient is unambiguously a
  // peptides user regardless of any stray feature/product-line values.
  if (auth?.session?.currentOrganizationPlan === "order-patient") {
    return "peptides";
  }

  const isPeptidesOnlyOrg = productLines?.peptides === true && productLines?.dme !== true && productLines?.encounters !== true;

  // Branch 1: Peptides-only short-circuit. If the org's productLines explicitly
  // say "peptides yes, dme no" we land on /peptides even when hasFeature
  // ("encounters") happens to be true — BE provisioning quirk where the
  // platform pre-creates a DoctorOffice with encounters=true. The org's own
  // product-line declaration is more authoritative than a stale features map.
  // See companion BE fix in Order - Provider - Signup.
  if (isPeptidesOnlyOrg && !blockPeptides) {
    return "peptides";
  }

  // Chiropractic shell: chiropractors get a fully separate, simplified
  // encounters experience (the ChiroApp sub-app). They still carry the
  // `encounters` feature, so this MUST be checked before the encounters
  // branch below, otherwise they'd land on the standard Home. Gated purely
  // on the provider's chosen specialty; nothing else in the matrix changes.
  if (auth?.user?.custom?.doctorType === "chiropractor") {
    return "chiro";
  }

  if (hasFeature("encounters")) {
    return "default";
  }
  if (hasFeature("ordersSalesDashboard") && hasSalesRepGroup(auth)) {
    return "salesRep";
  }
  if (hasFeature("ordersManagement")) {
    return "ordersManagement";
  }
  if (hasFeature("orders")) {
    // Branch 5b: peptides-only providers (peptides product line enabled, DME
    // disabled) should home into the peptides module rather than the DME
    // boards. Plan stays `order-provider`, so the `orders` feature is still
    // granted; we differentiate purely on `orgInfo.productLines`. See issue #1917.
    if (productLines?.peptides === true && productLines?.dme !== true && !blockPeptides) {
      return "peptides";
    }
    return "orders";
  }
  if (productLines?.peptides === true && !blockPeptides) {
    return "peptides";
  }
  return null;
}

function hasSalesRepGroup(auth) {
  // Mirrors the admin-bypass behavior of `hasGroup` in useAuth.js: admins
  // are treated as members of every group. Preserves the original
  // Routes.js:179 condition. Distinct from isNonAdminSalesRep, which is
  // explicitly the "non-admin" check — different intent, different shape.
  if (auth?.user?.role === "administrator") return true;
  const groups = auth?.user?.groups;
  if (!Array.isArray(groups)) return false;
  return groups.includes("sales-rep") || groups.includes("sales-rep-master");
}
